import moment from "moment";
import { getTrackerById } from "./trackerStorage";
import {
  getSubmissionPersonKey,
  getSubmissionsForTracker,
} from "./submissionStorage";
import {
  getMeasureTypeMeta,
  MEASURE_DIRECTIONS,
  QUESTION_MODES,
} from "./questions/questionStorage";

const WEIGHT_KEYS = /weight|body\s*fat|fat\s*%|bmi|waist|hip|chest|kg|lbs/i;
const SCORE_KEYS =
  /energy|adherence|sessions|score|push.?up|grip|time.?trial|lean\s*mass|inbody/i;

/** Pull first number from values like "82 kg", "28%", "7". */
export const parseMeasurableNumber = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const match = String(raw).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const n = Number(match[0]);
  return Number.isFinite(n) ? n : null;
};

export const parseMeasurableUnit = (raw) => {
  if (raw == null) return "";
  const text = String(raw);
  const match = text.match(
    /\d+(\.\d+)?\s*([a-zA-Z%]+(?:\s*[a-zA-Z]+)*)\s*$/
  );
  return match?.[2]?.trim() || "";
};

const normalizeLabel = (label = "") =>
  String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const collectTrackerMeasurableQuestions = (tracker) => {
  if (!tracker) return [];
  const out = [];
  const push = (q) => {
    if (!q) return;
    const measurable =
      q.measurable === true ||
      q.mode === QUESTION_MODES.MEASURABLE ||
      q.mode === "measurable";
    if (!measurable && !WEIGHT_KEYS.test(q.label || "")) return;
    out.push({
      id: q.id || q.questionId || normalizeLabel(q.label),
      label: q.label || "Measure",
      measureType: q.measureType || "",
      measureDirection:
        q.measureDirection ||
        getMeasureTypeMeta(q.measureType)?.defaultDirection ||
        MEASURE_DIRECTIONS.DESCENDING,
      unit: q.defaultUnit || (q.units || [])[0] || "",
    });
  };

  (tracker.personalInfoQuestions || []).forEach(push);
  (tracker.sharedQuestions || []).forEach(push);
  (tracker.questions || []).forEach(push);
  (tracker.forms || []).forEach((form) => {
    (form.questions || []).forEach(push);
  });
  return out;
};

const findAnswerValue = (answers = {}, label) => {
  if (!answers || typeof answers !== "object") return null;
  if (answers[label] != null) return answers[label];
  const want = normalizeLabel(label);
  const entry = Object.entries(answers).find(
    ([key]) =>
      normalizeLabel(key) === want ||
      normalizeLabel(key).includes(want) ||
      want.includes(normalizeLabel(key))
  );
  return entry ? entry[1] : null;
};

/**
 * Detect measurable-looking answer keys when tracker questions are sparse.
 */
const inferMetricLabelsFromAnswers = (submissions = []) => {
  const counts = new Map();
  submissions.forEach((row) => {
    Object.entries(row.answers || {}).forEach(([key, value]) => {
      const n = parseMeasurableNumber(value);
      if (n == null) return;
      if (!WEIGHT_KEYS.test(key) && !SCORE_KEYS.test(key)) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count >= 1)
    .map(([label]) => ({
      id: normalizeLabel(label),
      label,
      measureType: WEIGHT_KEYS.test(label) ? "weight" : "",
      measureDirection: WEIGHT_KEYS.test(label)
        ? MEASURE_DIRECTIONS.ASCENDING
        : MEASURE_DIRECTIONS.DESCENDING,
      unit: "",
    }));
};

const directionIsLowerBetter = (direction) =>
  direction === MEASURE_DIRECTIONS.ASCENDING || direction === "ascending";

/**
 * Build measurable performance series for the person on this submission's tracker.
 * Pass `historyRows` when data comes from the API (avoids localStorage).
 */
export const buildSubmissionPerformance = (submission, historyRows = null) => {
  if (!submission?.trackerId) {
    return { metrics: [], personKey: "", historyCount: 0 };
  }

  const personKey = getSubmissionPersonKey(submission);
  const pool = Array.isArray(historyRows)
    ? historyRows
    : getSubmissionsForTracker(submission.trackerId);
  const all = pool
    .filter((row) => getSubmissionPersonKey(row) === personKey)
    .sort(
      (a, b) =>
        new Date(a.submittedAt || a.createdAt || 0) -
        new Date(b.submittedAt || b.createdAt || 0)
    );

  const tracker = getTrackerById(submission.trackerId);
  let defs = collectTrackerMeasurableQuestions(tracker);
  if (!defs.length) {
    defs = inferMetricLabelsFromAnswers(all);
  }

  // Prefer Current Weight over Starting Weight when both exist as separate keys
  const metrics = defs
    .map((def) => {
      const points = [];
      all.forEach((row) => {
        let raw = findAnswerValue(row.answers, def.label);
        // Live weight aliases only — never pull Target Weight into Current/Starting
        const labelNorm = normalizeLabel(def.label);
        const isTargetWeight = /target/.test(labelNorm) && /weight/.test(labelNorm);
        if (
          raw == null &&
          /weight/i.test(def.label) &&
          !isTargetWeight
        ) {
          raw =
            findAnswerValue(row.answers, "Current Weight") ||
            findAnswerValue(row.answers, "Starting Weight") ||
            findAnswerValue(row.answers, "Weight");
        }
        const value = parseMeasurableNumber(raw);
        if (value == null) return;
        const unit =
          parseMeasurableUnit(raw) ||
          def.unit ||
          getMeasureTypeMeta(def.measureType)?.units?.[0] ||
          "";
        points.push({
          submissionId: row.id,
          formName: row.formName || "Submission",
          submittedAt: row.submittedAt || row.createdAt,
          value,
          unit,
          raw,
          isCurrent: row.id === submission.id,
        });
      });

      if (points.length < 1) return null;

      const first = points[0];
      const last = points[points.length - 1];
      const currentPoint =
        points.find((p) => p.isCurrent) || last;
      const delta = currentPoint.value - first.value;
      const pct =
        first.value !== 0 ? (delta / Math.abs(first.value)) * 100 : null;
      const lowerBetter = directionIsLowerBetter(def.measureDirection);
      const improved =
        Math.abs(delta) < 0.0001
          ? "flat"
          : lowerBetter
            ? delta < 0
              ? "up"
              : "down"
            : delta > 0
              ? "up"
              : "down";

      return {
        id: def.id,
        label: def.label.replace(/^Starting\s+/i, "").replace(/^Current\s+/i, "") ||
          def.label,
        measureType: def.measureType,
        measureDirection: def.measureDirection,
        unit: currentPoint.unit || first.unit || def.unit || "",
        points,
        baseline: first.value,
        current: currentPoint.value,
        delta,
        pctChange: pct,
        improved,
        lowerBetter,
      };
    })
    .filter(Boolean);

  // Deduplicate by normalized label (Starting Weight / Current Weight → Weight series)
  // Keep Target Weight separate from live Weight.
  const byKey = new Map();
  metrics.forEach((metric) => {
    let key = normalizeLabel(metric.label).replace(
      /^(starting|current)\s+/,
      ""
    );
    if (/target/.test(key) && /weight/.test(key)) {
      key = "target weight";
    }
    const prev = byKey.get(key);
    if (!prev || metric.points.length > prev.points.length) {
      byKey.set(key, {
        ...metric,
        label:
          key
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ") || metric.label,
      });
    }
  });

  const ranked = Array.from(byKey.values()).sort((a, b) => {
    const score = (m) =>
      (m.points.length >= 2 ? 100 : 0) +
      (m.improved !== "flat" ? 20 : 0) +
      (m.points.some((p) => p.isCurrent) ? 10 : 0) +
      m.points.length;
    return score(b) - score(a);
  });

  return {
    metrics: ranked,
    personKey,
    historyCount: all.length,
  };
};

export const formatMetricValue = (value, unit) => {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded =
    Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return unit ? `${rounded} ${unit}` : String(rounded);
};

export const formatDelta = (delta, unit, improved) => {
  if (delta == null || Number.isNaN(delta)) return "—";
  const sign = delta > 0 ? "+" : "";
  const rounded = Math.round(delta * 10) / 10;
  const text = unit ? `${sign}${rounded} ${unit}` : `${sign}${rounded}`;
  if (improved === "flat") return "No change";
  return text;
};

export const formatPointLabel = (point) => {
  if (point?.submittedAt) {
    return moment(point.submittedAt).format("DD MMM");
  }
  return point?.formName || "—";
};
